<template>
  <v-card class="pa-6">
    <v-card-title class="pa-0 ma-0">
      {{ $t('people.external_invitation.title') }}
    </v-card-title>

    <p class="body-2 blue-grey--text text--darken-1 mt-2 mb-6">
      {{ $t('people.external_invitation.message', { organizationName }) }}
    </p>

    <EmailField
      testId="inviteExternalUserForm-input-email"
      :disabled="isInviting"
      :error="emailError"
      :label="$t('common.email_one')"
      :value="email"
      @input="handleEmailInput"
    />

    <UserRolesSection
      data-testid="inviteExternalUserForm-rolesSection"
      :account="roleData"
      :canAssignRole="canAssignRole"
      :defaultSiteId="inviterSiteId"
      :rolesEnabled="rolesEnabled"
      @change="handleRoleDataChange"
    />

    <v-card-actions class="justify-end pa-0 mt-4">
      <Button
        plain
        color="blueGrey"
        data-testid="inviteExternalUserForm-button-back"
        :disabled="isInviting"
        :text="$t('common.button.back')"
        @click="$emit('clickBack')"
      />
      <Button
        data-testid="inviteExternalUserForm-button-send"
        :disabled="!canSubmit"
        :loading="isInviting"
        :text="$t('people.external_invitation.send')"
        @click="handleInvite"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import { utils } from 'frontend-js-shared'

import validationHelper from '@/lib/validationHelper'
import mixin from '@/mixins/mixin.vue'

import Button from '@/components/UI/Button.vue'
import EmailField from '@/components/Core/EmailField.vue'
import UserRolesSection from '@/components/People/UserRolesSection.vue'

const DUPLICATE_IN_ORG_STATUSES = new Set([406, 422])

const DEFAULT_ROLE_FIELD_VALUES = Object.freeze({
  isOrganizationAdmin: false,
  isSiteAdmin: false,
  isRoutineManager: false,
  isCoach: false,
  canDoFinancialValidation: false,
  managedSites: [],
  managedSiteGroups: [],
})

export default {
  name: 'InviteExternalUserForm',

  components: { Button, EmailField, UserRolesSection },

  mixins: [mixin],

  props: {
    canAssignRole: { type: Boolean, required: false, default: false },
    organizationId: { type: String, required: true },
    rolesEnabled: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },
  },

  data() {
    return {
      email: '',
      isInviting: false,
      roleData: { ...DEFAULT_ROLE_FIELD_VALUES },
    }
  },

  computed: {
    ...mapGetters(['organization']),
    ...mapState({ user: state => state.access.account }),

    organizationName() {
      return this.organization?.name ?? ''
    },

    inviterSiteId() {
      return utils.getEntityId(this.user?.site)
    },

    emailError() {
      if (_.isEmpty(this.email))
        return this.$t('validation.form.this_field_is_required')
      if (!validationHelper.isValidEmailAddress(this.email))
        return this.$t('onboarding.invite.invalid_email_address')
      if (
        this.$constants.organization.domainsBlacklist.includes(
          this.email.split('@')[1]
        )
      )
        return this.$t('organization.domain_is_insecure_to_add')
      return ''
    },

    canSubmit() {
      return this.email !== '' && this.emailError === '' && !this.isInviting
    },
  },

  methods: {
    ...mapActions({ createAccount: 'createAccount' }),

    handleEmailInput(value) {
      this.email = value
    },

    getInvitationErrorMessage(error) {
      const status = error?.response?.status
      if (DUPLICATE_IN_ORG_STATUSES.has(status))
        return this.$t('people.external_invitation.already_member')
      return this.$t('people.account_invitation_error')
    },

    handleRoleDataChange(changes) {
      this.roleData = { ...this.roleData, ...changes }
    },

    async handleInvite() {
      if (!this.canSubmit) return
      this.isInviting = true
      try {
        await this.createAccount({
          organizationId: this.organizationId,
          account: {
            email: this.email,
            site: this.inviterSiteId,
            ...this.roleData,
          },
        })
        this.showSuccess(this.$t('people.external_invitation.success'))
        this.$track('organization.Update', this.$store, {
          change: 'inviteExternalUser',
          page: 'people',
        })
        this.$emit('onUsersAdded')
        this.$emit('closeModal')
      } catch (error) {
        this.showError(this.getInvitationErrorMessage(error))
      } finally {
        this.isInviting = false
      }
    },
  },
}
</script>
